import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { SVGIcon, trashIcon } from '@progress/kendo-svg-icons';
import { filter, get, head, map } from 'lodash';
import { take } from 'rxjs';
import { IIepContributor } from 'src/app/modules/iep/iep-document/interfaces/iep-contributor';
import { IIepStaff } from 'src/app/modules/iep/iep-document/interfaces/iep-staff';
import { DocumentStatus } from 'src/app/modules/referrals/interfaces/referrals.model';
import {
  dialogAnimationDuration,
  dialogTimer,
} from 'src/app/shared/constants/dialog-specs';
import { NOTIFICATION_MESSAGES } from 'src/app/shared/constants/notification-messages';
import { OperationType } from 'src/app/shared/enums/operation-type';
import { StudentProfile } from 'src/app/shared/interfaces/student-profile';
import { ContributorRequest } from 'src/app/shared/models/add-contributor.model';
import { SubmitContributorData } from 'src/app/shared/models/submit-contributor.model';
import { User } from 'src/app/shared/models/user';
import { ContributorService } from 'src/app/shared/services/contributor-service';
import { AuthStore } from 'src/app/stores/auth.store';

@Component({
  selector: 'app-contributor-dailogbox',
  templateUrl: './contributor-dailogbox.component.html',
  styleUrls: ['./contributor-dailogbox.component.scss'],
  standalone: false,
})
export class ContributorDailogboxComponent
  extends DialogContentBase
  implements OnInit {
  public opened: boolean = false;
  form: FormGroup;
  existingContributorsList: IIepContributor[];
  iepFormSk: string;
  sectionId: number;
  contributors: IIepStaff[];
  dbn: string;
  contributorName: string;
  public remove: SVGIcon = trashIcon;
  submitData: SubmitContributorData[] = [];
  user: User;
  readonly operationTypeRemove: OperationType = OperationType.Delete;
  readonly operationTypeSave: OperationType = OperationType.Add;
  studentDetails: StudentProfile;

  constructor(
    private fb: FormBuilder,
    private contributorService: ContributorService,
    private notificationService: NotificationService,
    dialog: DialogRef,
    private authStore: AuthStore,
  ) {
    super(dialog);
    this.user = this.authStore.UserInfo;
  }

  ngOnInit(): void {
    this.form = this.fb.group({ addContributor: ['', Validators.required] });
    this.dbn = this.getDbn(this.studentDetails);
    this.contributorService
      .getContributors(this.dbn)

      .pipe(take(1))
      .subscribe((data: IIepStaff[]) => {
        this.contributors = data.map((staff: IIepStaff) => {
          staff.fullName = staff.firstName + ' ' + staff.lastName;
          return staff;
        });
      });
  }

  public close(): void {
    this.dialog.close();
  }

  removeContibutor(data: ContributorRequest): void {
    this.submitData.push(
      new SubmitContributorData({
        ...head(
          filter(
            this.existingContributorsList,
            (contributors: IIepContributor) =>
              contributors.personSk === data.personSk,
          ),
        ),
        operationType: this.operationTypeRemove,
        iepFormSk: this.iepFormSk,
        sectionId: this.sectionId,
      }),
    );
    this.existingContributorsList = filter(
      this.existingContributorsList,
      (contributors: IIepContributor) =>
        contributors.personSk !== data.personSk,
    );
  }

  save(): void {
    this.submitData.push(
      ...map(
        get(this.form.value, 'addContributor'),
        (d: SubmitContributorData) =>
          new SubmitContributorData({
            ...d,
            actionType: DocumentStatus.SaveForLater,
            operationType: this.operationTypeSave,
            iepFormSk: this.iepFormSk,
            sectionId: this.sectionId,
            lastModuser: this.user.username,
          } as any),
      ),
    );
    this.submitContributorDetailsService(this.submitData);
  }

  submitContributorDetailsService(
    submitRequestdata: SubmitContributorData[],
  ): void {
    this.contributorService
      .submit(submitRequestdata)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (response?.isRequestSucessful) {
            this.notificationService.show({
              content: NOTIFICATION_MESSAGES.submitSuccess,
              cssClass: 'button-notification',
              hideAfter: dialogTimer,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'success', icon: true },
            });
            this.dialog.close();
          } else {
            // 👇 Business error from API
            this.notificationService.show({
              content:
                response?.error || NOTIFICATION_MESSAGES.submissionFailed,
              hideAfter: dialogTimer,
              cssClass: 'notification-error',
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'error', icon: true },
            });
          }
        },
        error: () => {
          // 👇 Actual HTTP error (500, 404, network issue, etc.)
          this.notificationService.show({
            content: NOTIFICATION_MESSAGES.submissionFailed,
            cssClass: 'notification-error',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        },
      });
  }
  getDbn(student: StudentProfile): string {
    const schoolDBN = student?.schoolDBN;
    const cse = student?.cse;

    if (schoolDBN?.startsWith('88') && cse && cse !== 'N/A') {
      return `92CS${cse}`;
    }

    return schoolDBN;
  }
}
